import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import GrainIcon from '@mui/icons-material/Grain';
import UpdateIcon from '@mui/icons-material/Update';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { differenceInMinutes } from 'date-fns';
import { directionToDegrees, formatDateTime, getTimeRange, kphToMph } from '../utils/utils';
import { useLatestData } from '../hooks/useLatestData';
import { useLatestLightningData } from '../hooks/useLatestLightningData';
import { useHistoricalData } from '../hooks/useHistoricalData';
import { useInterval } from '../hooks/useInterval';
import { supabase } from '../supabaseClient';
import './TinticOperationsBoard.css';

const REFRESH_MINUTES = 5;
const normalizeLightningLevel = (value) => {
  if (value === 'Level 2') return 'Red';
  if (value === 'Level 1') return 'Yellow';
  return value || 'Normal';
};

const ensureUtcDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const iso = value.endsWith('Z') ? value : `${value}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toMetricPoints = (rows, selector) =>
  (rows || [])
    .map((row) => {
      const value = selector(row);
      if (value == null || Number.isNaN(Number(value))) return null;
      return {
        value: Number(value),
        timeUtc: row.time_utc,
      };
    })
    .filter((point) => point != null);

const formatRange = (points, decimals = 1) => {
  if (!points || points.length === 0) return 'N/A';
  const values = points.map((point) => point.value).filter((value) => !Number.isNaN(value));
  if (values.length === 0) return 'N/A';
  return `${Math.min(...values).toFixed(decimals)} to ${Math.max(...values).toFixed(decimals)}`;
};

const getPmCategory = (value, type) => {
  if (value == null || Number.isNaN(value)) return 'Unknown';
  if (type === 'pm25') {
    if (value <= 12) return 'Good';
    if (value <= 35.4) return 'Moderate';
    if (value <= 55.4) return 'USG';
    if (value <= 150.4) return 'Unhealthy';
    if (value <= 250.4) return 'Very Unhealthy';
    return 'Hazardous';
  }
  if (value <= 54) return 'Good';
  if (value <= 154) return 'Moderate';
  if (value <= 254) return 'USG';
  if (value <= 354) return 'Unhealthy';
  if (value <= 424) return 'Very Unhealthy';
  return 'Hazardous';
};

const pmPriority = {
  Unknown: 0,
  Good: 1,
  Moderate: 2,
  USG: 3,
  Unhealthy: 4,
  'Very Unhealthy': 5,
  Hazardous: 6,
};

const statusTone = {
  ok: '#34d399',
  monitor: '#fbbf24',
  warn: '#fb923c',
  critical: '#f87171',
};

const statCardTone = {
  ok: 'tone-ok',
  monitor: 'tone-monitor',
  warn: 'tone-warn',
  critical: 'tone-critical',
};

const WEATHER_PLACEHOLDERS = [
  { key: 'temperature', label: 'Temperature', unit: 'F', note: 'Ambient dry bulb' },
  { key: 'humidity', label: 'Humidity', unit: '%', note: 'Relative humidity' },
  { key: 'pressure', label: 'Pressure', unit: 'inHg', note: 'Station pressure' },
  { key: 'dewpoint', label: 'Dew Point', unit: 'F', note: 'Condensation threshold' },
  { key: 'precip', label: 'Precip (1h)', unit: 'in', note: 'Last hour accumulation' },
  { key: 'visibility', label: 'Visibility', unit: 'mi', note: 'Observed visibility' },
  { key: 'cloud', label: 'Cloud Cover', unit: '%', note: 'Sky coverage fraction' },
  { key: 'feels_like', label: 'Feels Like', unit: 'F', note: 'Heat/chill adjusted' },
];

const TREND_METRIC_OPTIONS = [
  {
    value: 'wind_gust',
    label: 'Wind Gust (mph)',
    unit: 'mph',
    stroke: '#7cf6c5',
    fill: 'rgba(124, 246, 197, 0.16)',
    decimals: 1,
  },
  {
    value: 'wind_speed',
    label: 'Wind Speed (mph)',
    unit: 'mph',
    stroke: '#63eab2',
    fill: 'rgba(99, 234, 178, 0.14)',
    decimals: 1,
  },
  {
    value: 'pm25',
    label: 'PM2.5 (ug/m3)',
    unit: 'ug/m3',
    stroke: '#8bc3ff',
    fill: 'rgba(139, 195, 255, 0.14)',
    decimals: 1,
  },
  {
    value: 'pm10',
    label: 'PM10 (ug/m3)',
    unit: 'ug/m3',
    stroke: '#92ddff',
    fill: 'rgba(146, 221, 255, 0.14)',
    decimals: 1,
  },
];

const TREND_RANGE_OPTIONS = [
  { value: 'last4h', label: 'Last 4 Hours' },
  { value: 'last12h', label: 'Last 12 Hours' },
  { value: 'last24h', label: 'Last 24 Hours' },
  { value: 'last3d', label: 'Last 3 Days' },
  { value: 'last7d', label: 'Last 7 Days' },
];

const PM_SEVERITY = {
  Unknown: 0,
  Good: 1,
  Moderate: 2,
  'Unhealthy for Sensitive Groups': 3,
  Unhealthy: 4,
  'Very Unhealthy': 5,
  Hazardous: 6,
};

const isPmThresholdExceeded = (status, threshold) =>
  (PM_SEVERITY[status] || 0) >= (PM_SEVERITY[threshold] || 0);

const getScriptAdvisory = ({
  lightningLevel,
  windStatus,
  pm25Status,
  pm10Status,
  pm25Threshold,
  pm10Threshold,
}) => {
  const pmAlert = isPmThresholdExceeded(pm25Status, pm25Threshold) || isPmThresholdExceeded(pm10Status, pm10Threshold);

  if (lightningLevel === 'Red') {
    return {
      tone: 'critical',
      title: 'Red Lightning Alert',
      message: 'Take shelter immediately. Mandatory 30 minute stand-down remains in effect until a Red all-clear message is issued.',
    };
  }

  if (lightningLevel === 'Orange') {
    return {
      tone: 'warn',
      title: 'Orange Lightning Alert',
      message: 'Cease high-risk activities, identify nearest shelter, and prepare for possible escalation to Red Alert.',
    };
  }

  if (lightningLevel === 'Yellow') {
    return {
      tone: 'monitor',
      title: 'Yellow Lightning Alert',
      message: 'Normal operations may continue with heightened awareness. Monitor conditions and prepare for possible escalation.',
    };
  }

  if (windStatus === 'High Gusts') {
    return {
      tone: 'warn',
      title: 'High Wind Gust Alert',
      message: 'High wind gusts detected. Secure items and be cautious of falling debris.',
    };
  }

  if (pmAlert) {
    return {
      tone: 'warn',
      title: 'Particulate Matter Alert',
      message: 'Wear appropriate PPE due to high particulate levels.',
    };
  }

  return {
    tone: 'ok',
    title: 'Normal',
    message: 'No active lightning alert. The board still monitors strikes within 20 miles while alerts begin at 18 miles.',
  };
};

const Sparkline = ({ points, stroke, fill, timezone, unit = '', decimals = 1, chartHeight = 120 }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  if (!points || points.length < 2) {
    return <Typography className="tintic-subtle">Trend unavailable</Typography>;
  }

  const values = points
    .map((point) => Number(point?.value))
    .filter((point) => !Number.isNaN(point));
  if (values.length < 2) {
    return <Typography className="tintic-subtle">Trend unavailable</Typography>;
  }

  const width = 640;
  const height = chartHeight;
  const plotTop = 8;
  const plotBottom = height - 10;
  const innerHeight = Math.max(plotBottom - plotTop, 1);
  const axisMin = 0;
  const maxValue = Math.max(...values);
  const headroom = Math.max(maxValue * 0.12, 1);
  const axisMax = Math.max(maxValue + headroom, 1);
  const range = Math.max(axisMax - axisMin, 1);

  const coordinates = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = plotBottom - ((value - axisMin) / range) * innerHeight;
    return { x, y };
  });

  const line = coordinates
    .map((coord) => {
      return `${coord.x},${coord.y}`;
    })
    .join(' ');

  const activeIndex = hoverIndex == null ? values.length - 1 : Math.max(0, Math.min(hoverIndex, values.length - 1));
  const activeCoord = coordinates[activeIndex];
  const activePoint = points[activeIndex];
  const activeValue = values[activeIndex];
  const tooltipLeftPercent = Math.min(Math.max((activeCoord.x / width) * 100, 8), 92);
  const markerLeftPercent = Math.min(Math.max((activeCoord.x / width) * 100, 0), 100);
  const markerTopPercent = Math.min(Math.max((activeCoord.y / height) * 100, 0), 100);
  const activeTimeLabel = activePoint?.timeUtc != null
    ? formatDateTime(activePoint.timeUtc, timezone)
    : 'N/A';

  const resolveIndexFromClientX = (clientX, currentTarget) => {
    const rect = currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const ratio = rect.width === 0 ? 0 : x / rect.width;
    return Math.round(ratio * (values.length - 1));
  };

  const handleMouseMove = (event) => {
    setHoverIndex(resolveIndexFromClientX(event.clientX, event.currentTarget));
  };

  const handleTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    setHoverIndex(resolveIndexFromClientX(touch.clientX, event.currentTarget));
  };

  return (
    <Box className="tintic-sparkline-wrap">
      <Typography className="tintic-sparkline-readout" sx={{ left: `${tooltipLeftPercent}%` }}>
        {activeTimeLabel} • {activeValue.toFixed(decimals)} {unit}
      </Typography>
      <Box
        className="tintic-sparkline-stage"
        style={{ height: `${chartHeight}px` }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
        onTouchStart={handleTouchMove}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setHoverIndex(null)}
      >
        <svg
          className="tintic-sparkline"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Metric trend"
        >
          {coordinates.map((coord, index) => (
            <line
              key={`bar-${index}`}
              x1={coord.x}
              x2={coord.x}
              y1={plotBottom}
              y2={coord.y}
              stroke="rgba(188, 236, 252, 0.2)"
              strokeWidth="1"
            />
          ))}
          <polyline points={`0,${plotBottom} ${line} ${width},${plotBottom}`} fill={fill} stroke="none" />
          <polyline points={line} fill="none" stroke={stroke} strokeWidth="4.2" strokeLinecap="round" />
        </svg>
        <Box
          className="tintic-sparkline-marker"
          sx={{
            left: `${markerLeftPercent}%`,
            top: `${markerTopPercent}%`,
            backgroundColor: stroke,
          }}
        />
      </Box>
    </Box>
  );
};

const WeatherVane = ({ direction = 'N' }) => {
  const normalizedDirection = typeof direction === 'string' && direction.trim() ? direction.trim().toUpperCase() : 'N';
  const safeDirection = normalizedDirection === 'N/A' ? 'N' : normalizedDirection;
  const degrees = directionToDegrees(safeDirection);

  return (
    <Box className="tintic-vane-wrap" aria-label={`Wind direction ${normalizedDirection}`}>
      <svg className="tintic-vane" viewBox="0 0 120 120" role="img" aria-hidden="true">
        <circle cx="60" cy="60" r="50" className="tintic-vane-ring" />
        <line x1="60" y1="12" x2="60" y2="24" className="tintic-vane-north-tick" />
        <text x="60" y="10" textAnchor="middle" className="tintic-vane-north-text">N</text>
        <g transform={`rotate(${degrees} 60 60)`}>
          <line x1="60" y1="84" x2="60" y2="34" className="tintic-vane-arrow" />
          <polygon points="60,24 53,38 67,38" className="tintic-vane-head" />
        </g>
        <circle cx="60" cy="60" r="5" className="tintic-vane-hub" />
      </svg>
    </Box>
  );
};

const TinticOperationsBoard = ({ siteName = 'Tintic', timezone = 'America/Denver' }) => {
  const [selectedTrendMetric, setSelectedTrendMetric] = useState('wind_gust');
  const [selectedTrendRange, setSelectedTrendRange] = useState('last12h');
  const [scriptState, setScriptState] = useState({
    lightningLevel: 'Normal',
    windStatus: 'Unknown',
    pm25Status: 'Unknown',
    pm10Status: 'Unknown',
    pm25Threshold: 'Unhealthy',
    pm10Threshold: 'Unhealthy',
    loading: true,
  });

  const {
    latestData,
    loading: latestLoading,
    error: latestError,
  } = useLatestData(siteName, REFRESH_MINUTES);
  const {
    lightningData,
    loading: lightningLoading,
    error: lightningError,
  } = useLatestLightningData(siteName, REFRESH_MINUTES);
  const { combinedData, loading: trendLoading } = useHistoricalData(
    siteName,
    selectedTrendRange,
    REFRESH_MINUTES
  );

  const rangedRows = useMemo(() => {
    const range = getTimeRange(selectedTrendRange);
    return (combinedData || []).filter((row) => {
      const time = Number(row?.time_utc);
      return !Number.isNaN(time) && time >= range.start && time <= range.end;
    });
  }, [combinedData, selectedTrendRange]);

  const latestTimestamp = useMemo(() => ensureUtcDate(latestData?.time_utc), [latestData]);
  const now = new Date();
  const ageMinutes = latestTimestamp ? Math.max(differenceInMinutes(now, latestTimestamp), 0) : null;

  const windSpeedMph = useMemo(() => kphToMph(latestData?.wind_speed_kph), [latestData]);
  const windGustMph = useMemo(() => kphToMph(latestData?.wind_gust_kph), [latestData]);
  const windDirection = latestData?.wind_direction || 'N/A';

  const windBand = useMemo(() => {
    const reference = Math.max(windSpeedMph || 0, windGustMph || 0);
    if (reference >= 35) return { level: 'critical', label: 'High Wind', hint: 'Use wind controls now' };
    if (reference >= 25) return { level: 'warn', label: 'Elevated Wind', hint: 'Watch active work fronts' };
    if (reference >= 15) return { level: 'monitor', label: 'Moderate Wind', hint: 'Monitor dust-generating tasks' };
    return { level: 'ok', label: 'Stable Wind', hint: 'Normal operations' };
  }, [windGustMph, windSpeedMph]);

  const pm25 = latestData?.pm2_5_ugm3 ?? null;
  const pm10 = latestData?.pm10_ugm3 ?? null;
  const pmCategory25 = getPmCategory(pm25, 'pm25');
  const pmCategory10 = getPmCategory(pm10, 'pm10');
  const worstPmCategory = pmPriority[pmCategory25] >= pmPriority[pmCategory10] ? pmCategory25 : pmCategory10;

  const pmBand = useMemo(() => {
    if (worstPmCategory === 'Hazardous' || worstPmCategory === 'Very Unhealthy') {
      return { level: 'critical', label: worstPmCategory };
    }
    if (worstPmCategory === 'Unhealthy' || worstPmCategory === 'USG') {
      return { level: 'warn', label: worstPmCategory };
    }
    if (worstPmCategory === 'Moderate') {
      return { level: 'monitor', label: worstPmCategory };
    }
    if (worstPmCategory === 'Good') {
      return { level: 'ok', label: worstPmCategory };
    }
    return { level: 'monitor', label: 'Unknown' };
  }, [worstPmCategory]);

  const lightningSignals = useMemo(() => {
    const referenceNow = new Date();
    const recent = (lightningData || [])
      .map((strike) => {
        const strikeTime = ensureUtcDate(strike.time_utc);
        const age = strikeTime ? Math.max(differenceInMinutes(referenceNow, strikeTime), 0) : Number.MAX_SAFE_INTEGER;
        return { ...strike, age };
      })
      .filter((strike) => strike.age <= 30 && strike.distance_km != null)
      .sort((a, b) => a.distance_km - b.distance_km);

    const nearest = recent[0] || null;
    const nearestMiles = nearest ? nearest.distance_km * 0.621371 : null;

    if (!nearest || nearestMiles > 20) {
      return {
        level: 'ok',
        label: 'No Nearby Lightning',
        sublabel: 'No strike within 20 miles (30 min)',
        strikes: recent.length,
      };
    }

    if (normalizeLightningLevel(scriptState.lightningLevel) === 'Red') {
      return {
        level: 'critical',
        label: 'Red Alert',
        sublabel: `Nearest ${nearestMiles.toFixed(1)} mi • red hold active`,
        strikes: recent.length,
      };
    }

    if (normalizeLightningLevel(scriptState.lightningLevel) === 'Orange') {
      return {
        level: 'warn',
        label: 'Orange Alert',
        sublabel: `Nearest ${nearestMiles.toFixed(1)} mi • 6-12 mi alert zone`,
        strikes: recent.length,
      };
    }

    if (normalizeLightningLevel(scriptState.lightningLevel) === 'Yellow') {
      return {
        level: 'monitor',
        label: 'Yellow Alert',
        sublabel: `Nearest ${nearestMiles.toFixed(1)} mi • 12-18 mi alert zone`,
        strikes: recent.length,
      };
    }

    return {
      level: 'monitor',
      label: 'Recent Lightning',
      sublabel: `Nearest ${nearestMiles.toFixed(1)} mi • no active alert`,
      strikes: recent.length,
    };
  }, [lightningData, scriptState.lightningLevel]);

  const windGustTrendPoints = useMemo(
    () => toMetricPoints(rangedRows, (point) => point.wind_gust_mph),
    [rangedRows]
  );

  const windSpeedTrendPoints = useMemo(
    () => toMetricPoints(rangedRows, (point) => point.wind_speed_mph),
    [rangedRows]
  );

  const pm25TrendPoints = useMemo(
    () => toMetricPoints(rangedRows, (point) => point.pm2_5_ugm3),
    [rangedRows]
  );

  const pm10TrendPoints = useMemo(
    () => toMetricPoints(rangedRows, (point) => point.pm10_ugm3),
    [rangedRows]
  );

  const trendSeriesMap = useMemo(
    () => ({
      wind_gust: windGustTrendPoints,
      wind_speed: windSpeedTrendPoints,
      pm25: pm25TrendPoints,
      pm10: pm10TrendPoints,
    }),
    [pm10TrendPoints, pm25TrendPoints, windGustTrendPoints, windSpeedTrendPoints]
  );

  const selectedTrendConfig = useMemo(
    () => TREND_METRIC_OPTIONS.find((option) => option.value === selectedTrendMetric) || TREND_METRIC_OPTIONS[0],
    [selectedTrendMetric]
  );

  const selectedTrendPoints = trendSeriesMap[selectedTrendConfig.value] || [];

  const fetchScriptState = useCallback(async () => {
    if (!siteName) return;

    try {
      const [actionResponse, thresholdResponse] = await Promise.all([
        supabase
          .from('current_action_levels')
          .select('lightning_action_level, wind_status, pm25_status, pm10_status')
          .eq('site_name', siteName)
          .single(),
        supabase
          .from('alert_thresholds')
          .select('pm25_threshold_category, pm10_threshold_category')
          .eq('site_name', siteName)
          .single(),
      ]);

      setScriptState({
        lightningLevel: normalizeLightningLevel(actionResponse?.data?.lightning_action_level),
        windStatus: actionResponse?.data?.wind_status || 'Unknown',
        pm25Status: actionResponse?.data?.pm25_status || 'Unknown',
        pm10Status: actionResponse?.data?.pm10_status || 'Unknown',
        pm25Threshold: thresholdResponse?.data?.pm25_threshold_category || 'Unhealthy',
        pm10Threshold: thresholdResponse?.data?.pm10_threshold_category || 'Unhealthy',
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch script advisory state:', error);
      setScriptState((prev) => ({ ...prev, loading: false }));
    }
  }, [siteName]);

  useEffect(() => {
    fetchScriptState();
  }, [fetchScriptState]);

  useInterval(
    () => {
      fetchScriptState();
    },
    REFRESH_MINUTES * 60 * 1000
  );

  const shiftAdvisory = useMemo(() => {
    if (scriptState.loading) {
      return {
        tone: 'monitor',
        title: 'Loading Script Status',
        message: 'Pulling wind, lightning, and particulate states from alert scripts.',
      };
    }

    return getScriptAdvisory(scriptState);
  }, [scriptState]);

  const isLoading = latestLoading || lightningLoading;
  const hasError = latestError || lightningError;

  if (isLoading && !latestData) {
    return (
      <Box className="tintic-shell tintic-center">
        <CircularProgress sx={{ color: '#87f2ca' }} />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box className="tintic-shell tintic-center">
        <Paper className="tintic-card tintic-error-card">
          <Typography variant="h6">Live Data Unavailable</Typography>
          <Typography variant="body2">{hasError}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="tintic-shell">
      <Box className="tintic-grid-overlay" />
      <Box className="tintic-wrap">
        <Paper className="tintic-card tintic-header-card">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            spacing={1.1}
          >
            <Box>
              <Typography className="tintic-kicker">Tintic Operations Board</Typography>
              <Typography className="tintic-subtle">
                Read-only live board • Auto refresh every {REFRESH_MINUTES} minutes
              </Typography>
            </Box>
            <Typography
              className="tintic-site-title"
              sx={{
                fontSize: 'clamp(2rem, 4.2vw, 3.8rem) !important',
                fontWeight: '800 !important',
                lineHeight: '0.9 !important',
                letterSpacing: '1px',
                textAlign: { xs: 'left', md: 'right' },
                alignSelf: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              {siteName}
            </Typography>
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Paper className={`tintic-card tintic-stat-card tintic-hero ${statCardTone[windBand.level]}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className="tintic-tile-label">Wind Conditions</Typography>
                  <Typography className="tintic-hero-value">{(windSpeedMph || 0).toFixed(1)} mph</Typography>
                  <Typography className="tintic-subtle">
                    Gust {(windGustMph || 0).toFixed(1)} mph • Direction {windDirection}
                  </Typography>
                </Box>
                <WeatherVane direction={windDirection} />
              </Stack>
              <Typography className="tintic-band-label" sx={{ color: statusTone[windBand.level] }}>
                {windBand.label}
              </Typography>
              <Typography className="tintic-subtle">{windBand.hint}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper className={`tintic-card tintic-stat-card tintic-hero ${statCardTone[shiftAdvisory.tone]}`}>
              <Typography className="tintic-tile-label">Shift Advisory</Typography>
              <Stack spacing={1.1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {shiftAdvisory.tone === 'ok' ? <CheckCircleRoundedIcon sx={{ color: statusTone.ok }} /> : <WarningAmberRoundedIcon sx={{ color: statusTone[shiftAdvisory.tone] || statusTone.monitor }} />}
                  <Typography className="tintic-value-compact" sx={{ fontSize: '1.2rem' }}>
                    {shiftAdvisory.title}
                  </Typography>
                </Stack>
                <Typography className="tintic-subtle">
                  {shiftAdvisory.message}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Paper className={`tintic-card tintic-stat-card ${statCardTone[pmBand.level]}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className="tintic-tile-label">Air Quality</Typography>
                  <Typography className="tintic-value-compact">{pmBand.label}</Typography>
                  <Typography className="tintic-subtle">
                    PM2.5 {pm25 == null ? 'N/A' : pm25.toFixed(1)} ug/m3 • PM10 {pm10 == null ? 'N/A' : pm10.toFixed(1)} ug/m3
                  </Typography>
                </Box>
                <GrainIcon className="tintic-tile-icon" />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Paper className={`tintic-card tintic-stat-card ${statCardTone[lightningSignals.level]}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className="tintic-tile-label">Lightning</Typography>
                  <Typography className="tintic-value-compact">{lightningSignals.label}</Typography>
                  <Typography className="tintic-subtle">
                    {lightningSignals.sublabel} • {lightningSignals.strikes} recent strike(s)
                  </Typography>
                </Box>
                <BoltIcon className="tintic-tile-icon" />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={12} lg={4}>
            <Paper className={`tintic-card tintic-stat-card ${ageMinutes != null && ageMinutes > 10 ? statCardTone.warn : statCardTone.ok}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className="tintic-tile-label">Data Freshness</Typography>
                  <Typography className="tintic-value-compact">
                    {ageMinutes == null ? 'N/A' : `${ageMinutes} min`}
                  </Typography>
                  <Typography className="tintic-subtle">
                    Last sample: {latestTimestamp ? formatDateTime(latestTimestamp.getTime(), timezone) : 'N/A'}
                  </Typography>
                </Box>
                <UpdateIcon className="tintic-tile-icon" />
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Typography className="tintic-subtle">
                  Refresh interval: {REFRESH_MINUTES} minutes
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className="tintic-card tintic-weather-placeholder">
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
                <Typography className="tintic-tile-label">Standard Weather Placeholders</Typography>
                <Typography className="tintic-subtle tintic-weather-pending">
                  Pending Xweather fields: wire these when endpoints/tables are ready
                </Typography>
              </Stack>
              <Grid container spacing={1.4}>
                {WEATHER_PLACEHOLDERS.map((metric) => (
                  <Grid item xs={12} sm={6} lg={3} key={metric.key}>
                    <Box className="tintic-placeholder-tile">
                      <Typography className="tintic-placeholder-label">{metric.label}</Typography>
                      <Typography className="tintic-placeholder-value">-- {metric.unit}</Typography>
                      <Typography className="tintic-placeholder-note">{metric.note}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className="tintic-card tintic-stat-card tintic-plot-mega">
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.1}
                className="tintic-plot-header"
              >
                <Box>
                  <Typography className="tintic-tile-label">Time Series</Typography>
                </Box>
                <FormControl size="small" className="tintic-select-control">
                  <Select
                    value={selectedTrendMetric}
                    onChange={(event) => setSelectedTrendMetric(event.target.value)}
                  >
                    {TREND_METRIC_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" className="tintic-select-control">
                  <Select
                    value={selectedTrendRange}
                    onChange={(event) => setSelectedTrendRange(event.target.value)}
                  >
                    {TREND_RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {trendLoading ? (
                <Typography className="tintic-subtle">Loading trend...</Typography>
              ) : (
                <>
                  <Sparkline
                    points={selectedTrendPoints}
                    stroke={selectedTrendConfig.stroke}
                    fill={selectedTrendConfig.fill}
                    timezone={timezone}
                    unit={selectedTrendConfig.unit}
                    decimals={selectedTrendConfig.decimals}
                    chartHeight={300}
                  />
                  <Typography className="tintic-subtle">
                    Range {formatRange(selectedTrendPoints, selectedTrendConfig.decimals)}
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TinticOperationsBoard;
